'use client'

import { useId } from 'react'

interface OrbitLoaderProps {
  size?: number
  label?: string
  fullscreen?: boolean
  className?: string
}

export function OrbitLoader({ size = 44, label, fullscreen = false, className }: OrbitLoaderProps) {
  const id = useId()

  // Ring: cx=64,cy=64, r=44, strokeWidth=16 (identical to the logo mark)
  // Circumference ≈ 276.46; gap arc ≈ 50.8 (matches logo mask cutout r=24)
  // stroke-dasharray replaces the mask so the gap rotates with the group
  const C = 2 * Math.PI * 44          // 276.46
  const gap = 50.8
  const dash = +(C - gap).toFixed(2)  // 225.66
  // dashoffset=82 centers the gap at the satellite position (~220° from 3 o'clock)
  const dashOffset = 82

  const loader = (
    <div
      className={`flex flex-col items-center justify-center gap-3 ${className || ''}`}
      role="status"
      aria-label={label || 'Loading'}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 128 128"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient
            id={`${id}-g`}
            x1="19" y1="13" x2="109" y2="115"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%"   stopColor="hsl(var(--primary))" stopOpacity="0.7" />
            <stop offset="55%"  stopColor="hsl(var(--primary))" stopOpacity="1" />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="1" />
          </linearGradient>
        </defs>

        {/* Entire orbit mark rotates as one unit — gap stays locked on the satellite */}
        <g className="of-orbit-spin" style={{ transformOrigin: '64px 64px' }}>
          {/* Ring: gap via stroke-dasharray so it travels with the group */}
          <circle
            cx="64" cy="64" r="44"
            stroke={`url(#${id}-g)`}
            strokeWidth="16"
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={dashOffset}
          />
          {/* Satellite sitting in the gap */}
          <circle
            cx="29" cy="34" r="14"
            stroke={`url(#${id}-g)`}
            strokeWidth="16"
          />
        </g>
      </svg>
      {label && <span className="of-t-meta">{label}</span>}
    </div>
  )

  if (fullscreen) {
    return <div className="flex min-h-[55vh] w-full items-center justify-center">{loader}</div>
  }
  return loader
}
